import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  ActivityIndicator,
  Platform,
  Alert,
  Image,
  TextInput,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { UserSessionAttributes } from '../../models/user-session-model';
import { ChargerAttributes } from '../../models/charger-model';
import {
  AttachmentObj,
  createEmptyAttachmentObj,
  FileTypeCategory,
} from '../../models/file-handler-model';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { getAllChargers } from '../../store/actions/charging-actions';
import {
  uploadFileToServer,
  downloadFilesFromServer,
  deleteFileFromServer,
  getListOfFiles,
} from '../common/utils/file-handler';
import { resizeImageIfNeeded } from '../../utils/image-resize-utils';

const FileUploaderTest: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const userSession: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user?.session
  );
  const chargers: ChargerAttributes[] = useSelector(
    (state: RootState) => state.chargers?.chargers || []
  );

  const [files, setFiles] = useState<AttachmentObj[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [selectedCharger, setSelectedCharger] = useState<ChargerAttributes | null>(null);
  const [previewImage, setPreviewImage] = useState<{ 
    uri: string; 
    file: any;
    metadata?: {
      originalSize: number;
      resizedSize?: number;
      compressedSize?: number;
      mimeType: string;
      compressionRatio?: number;
      wasResized?: boolean;
    }
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showChargerDropdown, setShowChargerDropdown] = useState(false);

  useEffect(() => {
    // Load chargers on mount
    dispatch(getAllChargers());
  }, [dispatch]);

  useEffect(() => {
    // Load files when charger is selected
    if (selectedCharger) {
      loadFiles();
    }
  }, [selectedCharger]);

  const loadFiles = async () => {
    if (!selectedCharger || !selectedCharger.uuid) return;
    
    setLoading(true);
    try {
      const fileList = await getListOfFiles(selectedCharger.uuid);
      if (fileList) {
        setFiles(fileList);
      }
    } catch (error) {
      console.error('Error loading files:', error);
      showAlert('Error', 'Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const filteredChargers = chargers.filter((charger) => {
    const searchLower = searchQuery.toLowerCase();
    const name = charger.name?.toLowerCase() || '';
    const serial = charger.unit_serial?.toLowerCase() || '';
    return name.includes(searchLower) || serial.includes(searchLower);
  });

  const handleChargerSelect = (charger: ChargerAttributes) => {
    setSelectedCharger(charger);
    setShowChargerDropdown(false);
    setSearchQuery('');
    setFiles([]); // Clear previous files
  };

  const handleClearPreview = () => {
    setPreviewImage(null);
  };

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      alert(`${title}: ${message}`);
    } else {
      Alert.alert(title, message);
    }
  };

  const handlePickImage = async () => {
    // Check if charger is selected
    if (!selectedCharger) {
      showAlert('Charger Required', 'Please select a charger before picking an image.');
      return;
    }

    // Prevent multiple simultaneous operations
    if (loading) {
      return;
    }

    try {
      // Request permission on native platforms
      if (Platform.OS !== 'web') {
        try {
          const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
          
          if (status !== 'granted') {
            showAlert('Permission Required', 'Camera roll access is required to pick images. Please enable it in Settings.');
            return;
          }
        } catch (permError) {
          console.error('Permission request error:', permError);
          showAlert('Permission Error', 'Failed to request permissions. Please check your device settings.');
          return;
        }
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        allowsEditing: false,
        quality: 1,
        base64: false, // Don't load base64 to prevent memory issues
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        const originalSize = asset.fileSize || 0;
        const mimeType = asset.mimeType || 'image/jpeg';
        
        // Step 1: Resize if needed
        let finalUri = asset.uri;
        let resizedSize = originalSize;
        let wasResized = false;
        
        if (asset.width && asset.height && 
            (asset.width > 1920 || asset.height > 1920)) {
          console.log(`Image is large (${asset.width}x${asset.height}), resizing...`);
          
          try {
            finalUri = await resizeImageIfNeeded(
              asset.uri, 
              asset.width, 
              asset.height
            );
            
            // Get new file size after resizing
            if (Platform.OS !== 'web') {
              const FileSystem = require('expo-file-system');
              const info = await FileSystem.getInfoAsync(finalUri);
              resizedSize = info.size || originalSize;
            } else {
              const response = await fetch(finalUri);
              const blob = await response.blob();
              resizedSize = blob.size;
            }
            
            wasResized = finalUri !== asset.uri;
            console.log(`Image resized: ${originalSize} bytes -> ${resizedSize} bytes (${((1 - resizedSize/originalSize) * 100).toFixed(1)}% reduction)`);
          } catch (resizeError) {
            console.error('Error resizing image:', resizeError);
            // Continue with original image
          }
        } else {
          console.log(`Image is small enough (${asset.width}x${asset.height}), no resize needed`);
        }
        
        // Step 2: Calculate compressed size for metadata (using resized image)
        let compressedSize: number | undefined;
        let compressionRatio: number | undefined;
        
        try {
          // For native, we need to read the file to estimate compression
          if (Platform.OS !== 'web' && finalUri) {
            const JSZip = require('jszip');
            const FileSystem = require('expo-file-system');
            
            // Read file as base64
            const base64 = await FileSystem.readAsStringAsync(finalUri, {
              encoding: FileSystem.EncodingType.Base64,
            });
            
            // Create ZIP
            const zip = new JSZip();
            const fileName = asset.fileName || 'file';
            zip.file(fileName, base64, { base64: true });
            
            // Generate compressed blob
            const zipBlob = await zip.generateAsync({ 
              type: 'uint8array',
              compression: 'DEFLATE',
              compressionOptions: { level: 6 }
            });
            
            compressedSize = zipBlob.length;
            if (compressedSize !== undefined) {
              compressionRatio = resizedSize > 0 ? (compressedSize / resizedSize) * 100 : 100;
            }
          } else if (Platform.OS === 'web') {
            // For web, we can directly access the File object
            const JSZip = require('jszip');
            const response = await fetch(finalUri);
            const blob = await response.blob();
            
            const zip = new JSZip();
            const fileName = asset.fileName || 'file';
            zip.file(fileName, blob);
            
            const zipBlob = await zip.generateAsync({ 
              type: 'blob',
              compression: 'DEFLATE',
              compressionOptions: { level: 6 }
            });
            
            compressedSize = zipBlob.size;
            if (compressedSize !== undefined) {
              compressionRatio = resizedSize > 0 ? (compressedSize / resizedSize) * 100 : 100;
            }
          }
        } catch (compressionError) {
          console.error('Error calculating compression:', compressionError);
          // Continue without compression info
        }
        
        // Step 3: Create updated file object with resized URI
        const updatedFile = {
          ...asset,
          uri: finalUri,
          fileSize: resizedSize,
        };
        
        // Set preview with metadata
        setPreviewImage({
          uri: finalUri,
          file: updatedFile,
          metadata: {
            originalSize,
            resizedSize: wasResized ? resizedSize : undefined,
            compressedSize,
            mimeType,
            compressionRatio,
            wasResized,
          }
        });
      }
    } catch (error) {
      console.error('Error in handlePickImage:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showAlert('Error', `Failed to pick image: ${errorMessage}`);
    }
  };

  const handleConfirmUpload = async () => {
    if (!previewImage || !selectedCharger) return;

    await handleUploadFile(previewImage.file);
    setPreviewImage(null); // Clear preview after upload
  };

  const handlePickDocument = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['image/*', 'application/pdf'],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        await handleUploadFile(asset as any);
      }
    } catch (error) {
      console.error('Error picking document:', error);
      showAlert('Error', 'Failed to pick document');
    }
  };

  const handleUploadFile = async (file: any) => {
    setLoading(true);
    setUploadProgress(0);

    try {
      // Validate file object
      if (!file || !file.uri) {
        throw new Error('Invalid file object: missing URI');
      }

      const fileName = file.name || file.fileName || 'uploaded_file';
      const fileUri = file.uri;
      const fileType = file.mimeType || file.type || 'application/octet-stream';
      const fileSize = file.size || file.fileSize || 0;

      // Validate user session
      if (!userSession?.id) {
        throw new Error('User session not found');
      }

      // Validate charger is selected
      if (!selectedCharger) {
        throw new Error('No charger selected');
      }

      const attachObj = createEmptyAttachmentObj({
        documentname: fileName,
        identifier: 'admin-test',
        fileversion: 1,
        userid: userSession.id.toString(),
        chargerid: selectedCharger.uuid,
        uploadedby: userSession.id.toString(),
      });

      let result: number | string;

      if (Platform.OS === 'web') {
        // Web: Convert URI to File object
        const response = await fetch(fileUri);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }
        const blob = await response.blob();
        const webFile = new File([blob], fileName, { type: fileType });
        
        result = await uploadFileToServer(
          webFile as any,
          attachObj,
          {
            maxSizeBytes: 50 * 1024 * 1024, // 50MB
            allowedTypes: FileTypeCategory.ANY,
          }
        );
      } else {
        // Native: Use file info
        result = await uploadFileToServer(
          {
            uri: fileUri,
            name: fileName,
            type: fileType,
            size: fileSize,
          },
          attachObj,
          {
            maxSizeBytes: 50 * 1024 * 1024, // 50MB
            allowedTypes: FileTypeCategory.ANY,
          }
        );
      }

      if (result === 0) {
        showAlert('Success', 'File uploaded successfully!');
        await loadFiles();
      } else {
        console.error('Upload failed:', result);
        showAlert('Upload Failed', typeof result === 'string' ? result : 'Unknown error');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      showAlert('Error', `Upload failed: ${errorMessage}`);
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const handleDownloadFile = async (file: AttachmentObj) => {
    setLoading(true);
    try {
      if (Platform.OS === 'web') {
        // Web: Uses file-saver to trigger download
        await downloadFilesFromServer(file);
        showAlert('Success', 'File download started');
      } else {
        // Native: Returns local file URI
        const localUri = await downloadFilesFromServer(file);
        showAlert('Success', `File downloaded to: ${localUri}`);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      showAlert('Error', `Download failed: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFile = async (file: AttachmentObj) => {
    if (Platform.OS === 'web') {
      const confirmed = window.confirm(
        `Are you sure you want to delete "${file.documentname}"?`
      );
      if (!confirmed) return;
    } else {
      Alert.alert(
        'Confirm Delete',
        `Are you sure you want to delete "${file.documentname}"?`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => await performDelete(file),
          },
        ]
      );
      return;
    }

    await performDelete(file);
  };

  const performDelete = async (file: AttachmentObj) => {
    setLoading(true);
    try {
      const success = await deleteFileFromServer(file);
      if (success) {
        showAlert('Success', 'File deleted successfully');
        await loadFiles();
      } else {
        showAlert('Error', 'Failed to delete file');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      showAlert('Error', `Delete failed: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch {
      return 'Unknown';
    }
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
    >
      {/* Charger Selection */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Select Charger</Text>
        <Text style={styles.sectionDescription}>
          Choose a charger to associate with uploaded files
        </Text>

        <Pressable
          style={styles.chargerSelector}
          onPress={() => setShowChargerDropdown(!showChargerDropdown)}
        >
          <View style={styles.chargerSelectorContent}>
            {selectedCharger ? (
              <>
                <MaterialIcons name="ev-station" size={20} color="#95F5AA" />
                <Text style={styles.chargerSelectorText}>
                  {selectedCharger.name}: {selectedCharger.unit_serial}
                </Text>
              </>
            ) : (
              <>
                <MaterialIcons name="ev-station" size={20} color="#666" />
                <Text style={[styles.chargerSelectorText, { color: '#666' }]}>
                  Select a charger...
                </Text>
              </>
            )}
          </View>
          <MaterialIcons
            name={showChargerDropdown ? 'expand-less' : 'expand-more'}
            size={24}
            color="white"
          />
        </Pressable>

        {showChargerDropdown && (
          <View style={styles.dropdown}>
            <TextInput
              style={styles.searchInput}
              placeholder="Search chargers..."
              placeholderTextColor="#666"
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
            <ScrollView style={styles.dropdownList} nestedScrollEnabled>
              {filteredChargers.length > 0 ? (
                filteredChargers.map((charger) => (
                  <Pressable
                    key={charger.uuid}
                    style={({ pressed }) => [
                      styles.dropdownItem,
                      pressed && styles.dropdownItemPressed,
                    ]}
                    onPress={() => handleChargerSelect(charger)}
                  >
                    <MaterialIcons name="ev-station" size={18} color="#95F5AA" />
                    <Text style={styles.dropdownItemText}>
                      {charger.name}: {charger.unit_serial}
                    </Text>
                  </Pressable>
                ))
              ) : (
                <Text style={styles.noResultsText}>No chargers found</Text>
              )}
            </ScrollView>
          </View>
        )}
      </View>

      {/* Upload Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Upload Test Files</Text>
        <Text style={styles.sectionDescription}>
          {selectedCharger
            ? 'Select an image to preview before uploading'
            : 'Please select a charger first'}
        </Text>

        {!previewImage && (
          <View style={styles.uploadButtons}>
            <Pressable
              style={({ pressed }) => [
                styles.uploadButton,
                (!selectedCharger || loading) && styles.uploadButtonDisabled,
                pressed && selectedCharger && styles.uploadButtonPressed,
              ]}
              onPress={handlePickImage}
              disabled={loading || !selectedCharger}
            >
              <MaterialIcons name="image" size={24} color="white" />
              <Text style={styles.uploadButtonText}>Pick Image</Text>
            </Pressable>

            <Pressable
              style={({ pressed }) => [
                styles.uploadButton,
                (!selectedCharger || loading) && styles.uploadButtonDisabled,
                pressed && selectedCharger && styles.uploadButtonPressed,
              ]}
              onPress={handlePickDocument}
              disabled={loading || !selectedCharger}
            >
              <MaterialIcons name="attach-file" size={24} color="white" />
              <Text style={styles.uploadButtonText}>Pick Document</Text>
            </Pressable>
          </View>
        )}

        {/* Image Preview */}
        {previewImage && (
          <View style={styles.previewContainer}>
            <View style={styles.previewImageWrapper}>
              <Image
                source={{ uri: previewImage.uri }}
                style={styles.previewImage}
                resizeMode="cover"
              />
              <Pressable
                style={styles.cancelButton}
                onPress={handleClearPreview}
              >
                <View style={styles.cancelButtonCircle}>
                  <MaterialIcons name="close" size={20} color="white" />
                </View>
              </Pressable>
            </View>
            
            {/* File Metadata Display */}
            {previewImage.metadata && (
              <View style={styles.metadataContainer}>
                <Text style={styles.metadataTitle}>File Information</Text>
                
                <View style={styles.metadataRow}>
                  <MaterialIcons name="category" size={16} color="#95F5AA" />
                  <Text style={styles.metadataLabel}>MIME Type:</Text>
                  <Text style={styles.metadataValue}>{previewImage.metadata.mimeType}</Text>
                </View>
                
                <View style={styles.metadataRow}>
                  <MaterialIcons name="photo-size-select-small" size={16} color="#95F5AA" />
                  <Text style={styles.metadataLabel}>Original Size:</Text>
                  <Text style={styles.metadataValue}>
                    {formatFileSize(previewImage.metadata.originalSize)}
                  </Text>
                </View>
                
                {previewImage.metadata.wasResized && previewImage.metadata.resizedSize !== undefined && (
                  <>
                    <View style={styles.metadataRow}>
                      <MaterialIcons name="photo-size-select-large" size={16} color="#00D9FF" />
                      <Text style={styles.metadataLabel}>Resized To:</Text>
                      <Text style={[styles.metadataValue, { color: '#00D9FF' }]}>
                        {formatFileSize(previewImage.metadata.resizedSize)}
                      </Text>
                    </View>
                    
                    <View style={styles.infoBox}>
                      <MaterialIcons name="info" size={16} color="#00D9FF" />
                      <Text style={styles.infoBoxText}>
                        Image was automatically resized to 1920px for faster uploads
                      </Text>
                    </View>
                  </>
                )}
                
                {previewImage.metadata.compressedSize !== undefined && (
                  <>
                    <View style={styles.metadataRow}>
                      <MaterialIcons name="compress" size={16} color="#00D9FF" />
                      <Text style={styles.metadataLabel}>Compressed Size:</Text>
                      <Text style={styles.metadataValue}>
                        {formatFileSize(previewImage.metadata.compressedSize)}
                      </Text>
                    </View>
                    
                    <View style={styles.metadataRow}>
                      <MaterialIcons name="trending-down" size={16} color={
                        previewImage.metadata.compressionRatio! < 100 ? '#95F5AA' : '#FF9800'
                      } />
                      <Text style={styles.metadataLabel}>ZIP Compression:</Text>
                      <Text style={[
                        styles.metadataValue,
                        { color: previewImage.metadata.compressionRatio! < 100 ? '#95F5AA' : '#FF9800' }
                      ]}>
                        {previewImage.metadata.compressionRatio!.toFixed(1)}%
                        {previewImage.metadata.compressionRatio! >= 100 && ' (no reduction)'}
                      </Text>
                    </View>
                    
                    {previewImage.metadata.compressionRatio! >= 100 && (
                      <View style={styles.warningBox}>
                        <MaterialIcons name="warning" size={16} color="#FF9800" />
                        <Text style={styles.warningText}>
                          This file is already compressed. ZIP may add overhead.
                        </Text>
                      </View>
                    )}
                  </>
                )}
              </View>
            )}
            
            <Pressable
              style={({ pressed }) => [
                styles.confirmUploadButton,
                pressed && styles.confirmUploadButtonPressed,
              ]}
              onPress={handleConfirmUpload}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <>
                  <MaterialIcons name="cloud-upload" size={24} color="white" />
                  <Text style={styles.confirmUploadButtonText}>Upload Image</Text>
                </>
              )}
            </Pressable>
          </View>
        )}

        {uploadProgress !== null && (
          <View style={styles.progressContainer}>
            <View style={styles.progressBar}>
              <View
                style={[styles.progressFill, { width: `${uploadProgress}%` }]}
              />
            </View>
            <Text style={styles.progressText}>{uploadProgress}%</Text>
          </View>
        )}
      </View>

      {/* Files List Section */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <View>
            <Text style={styles.sectionTitle}>Uploaded Files</Text>
            <Text style={styles.sectionDescription}>
              {selectedCharger
                ? `${selectedCharger.name}: ${selectedCharger.unit_serial}`
                : 'Select a charger to view files'}
            </Text>
          </View>
          <Pressable
            style={styles.refreshButton}
            onPress={loadFiles}
            disabled={loading}
          >
            <MaterialIcons
              name="refresh"
              size={24}
              color="#95F5AA"
            />
          </Pressable>
        </View>

        {loading && files.length === 0 ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#00D9FF" />
            <Text style={styles.loadingText}>Loading files...</Text>
          </View>
        ) : files.length === 0 ? (
          <View style={styles.emptyContainer}>
            <MaterialIcons name="folder-open" size={64} color="#555" />
            <Text style={styles.emptyText}>No files uploaded yet</Text>
            <Text style={styles.emptySubtext}>
              Upload a file to test the file handler
            </Text>
          </View>
        ) : (
          <View style={styles.filesList}>
            {files.map((file) => (
              <View key={file.documentid} style={styles.fileCard}>
                <View style={styles.fileHeader}>
                  <MaterialIcons
                    name="insert-drive-file"
                    size={24}
                    color="#95F5AA"
                  />
                  <View style={styles.fileInfo}>
                    <Text style={styles.fileName} numberOfLines={1}>
                      {file.documentname}
                    </Text>
                    <Text style={styles.fileDetails}>
                      {formatFileSize(file.size)} • ID: {file.documentid}
                    </Text>
                    {file.checksum && (
                      <Text style={styles.fileHash} numberOfLines={1}>
                        SHA-256: {file.checksum.substring(0, 16)}...
                      </Text>
                    )}
                  </View>
                </View>

                <View style={styles.fileActions}>
                  <Pressable
                    style={({ pressed }) => [
                      styles.actionButton,
                      styles.downloadButton,
                      pressed && styles.actionButtonPressed,
                    ]}
                    onPress={() => handleDownloadFile(file)}
                    disabled={loading}
                  >
                    <MaterialIcons name="download" size={20} color="white" />
                    <Text style={styles.actionButtonText}>Download</Text>
                  </Pressable>

                  <Pressable
                    style={({ pressed }) => [
                      styles.actionButton,
                      styles.deleteButton,
                      pressed && styles.actionButtonPressed,
                    ]}
                    onPress={() => handleDeleteFile(file)}
                    disabled={loading}
                  >
                    <MaterialIcons name="delete" size={20} color="white" />
                    <Text style={styles.actionButtonText}>Delete</Text>
                  </Pressable>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>

      {/* Info Section */}
      <View style={styles.infoSection}>
        <MaterialIcons name="info-outline" size={20} color="#00D9FF" />
        <Text style={styles.infoText}>
          This test uses the file handler with compression, SHA-256 verification,
          and malware scanning (backend). Files are uploaded to a test charger ID.
        </Text>
      </View>

      {/* Global Loading Overlay */}
      {loading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#00D9FF" />
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1E1E1E',
  },
  contentContainer: {
    padding: 16,
    gap: 20,
  },
  section: {
    backgroundColor: '#2A2A2A',
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: '#00D9FF33',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: 'white',
    marginBottom: 8,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#AAA',
    lineHeight: 20,
  },
  uploadButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
  },
  uploadButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#3FC67D',
    padding: 16,
    borderRadius: 8,
    gap: 8,
  },
  uploadButtonPressed: {
    backgroundColor: '#00B8D9',
    transform: [{ scale: 0.98 }],
  },
  uploadButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
  progressContainer: {
    marginTop: 16,
  },
  progressBar: {
    height: 8,
    backgroundColor: '#333',
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#00D9FF',
  },
  progressText: {
    fontSize: 14,
    color: '#AAA',
    textAlign: 'center',
    marginTop: 8,
  },
  refreshButton: {
    padding: 8,
  },
  loadingContainer: {
    alignItems: 'center',
    padding: 32,
  },
  loadingText: {
    fontSize: 14,
    color: '#AAA',
    marginTop: 12,
  },
  emptyContainer: {
    alignItems: 'center',
    padding: 32,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#AAA',
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
    textAlign: 'center',
  },
  filesList: {
    gap: 12,
  },
  fileCard: {
    backgroundColor: '#333',
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: '#444',
  },
  fileHeader: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  fileInfo: {
    flex: 1,
  },
  fileName: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
    marginBottom: 4,
  },
  fileDetails: {
    fontSize: 12,
    color: '#AAA',
    marginBottom: 4,
  },
  fileHash: {
    fontSize: 11,
    color: '#666',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  fileActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 6,
    gap: 6,
  },
  actionButtonPressed: {
    transform: [{ scale: 0.96 }],
  },
  downloadButton: {
    backgroundColor: '#00D9FF',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
  },
  infoSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#2A2A2A',
    borderRadius: 12,
    padding: 16,
    gap: 12,
    borderWidth: 1,
    borderColor: '#00D9FF33',
  },
  infoText: {
    flex: 1,
    fontSize: 13,
    color: '#AAA',
    lineHeight: 18,
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Charger Selector Styles
  chargerSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#333',
    padding: 16,
    borderRadius: 8,
    marginTop: 16,
    borderWidth: 1,
    borderColor: '#444',
  },
  chargerSelectorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  chargerSelectorText: {
    fontSize: 16,
    color: 'white',
    flex: 1,
  },
  dropdown: {
    marginTop: 8,
    backgroundColor: '#333',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#444',
    maxHeight: 300,
  },
  searchInput: {
    backgroundColor: '#2A2A2A',
    color: 'white',
    padding: 12,
    borderRadius: 8,
    margin: 8,
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#444',
  },
  dropdownList: {
    maxHeight: 250,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#2A2A2A',
  },
  dropdownItemPressed: {
    backgroundColor: '#2A2A2A',
  },
  dropdownItemText: {
    fontSize: 15,
    color: 'white',
    flex: 1,
  },
  noResultsText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    padding: 20,
  },
  uploadButtonDisabled: {
    backgroundColor: '#555',
    opacity: 0.5,
  },
  // Preview Styles
  previewContainer: {
    marginTop: 16,
    gap: 12,
  },
  previewImageWrapper: {
    position: 'relative',
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#333',
  },
  previewImage: {
    width: '100%',
    height: 300,
    borderRadius: 12,
  },
  cancelButton: {
    position: 'absolute',
    top: 12,
    right: 12,
  },
  cancelButtonCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'white',
  },
  confirmUploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#3FC67D',
    padding: 16,
    borderRadius: 8,
    gap: 8,
  },
  confirmUploadButtonPressed: {
    backgroundColor: '#2EA865',
    transform: [{ scale: 0.98 }],
  },
  confirmUploadButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
  // Metadata Styles
  metadataContainer: {
    backgroundColor: '#1E1E1E',
    borderRadius: 8,
    padding: 16,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#444',
  },
  metadataTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
    marginBottom: 12,
  },
  metadataRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    gap: 8,
  },
  metadataLabel: {
    fontSize: 14,
    color: '#AAA',
    minWidth: 120,
  },
  metadataValue: {
    fontSize: 14,
    color: 'white',
    fontWeight: '500',
    flex: 1,
  },
  warningBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 152, 0, 0.1)',
    padding: 10,
    borderRadius: 6,
    marginTop: 8,
    gap: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 152, 0, 0.3)',
  },
  warningText: {
    flex: 1,
    fontSize: 12,
    color: '#FF9800',
    lineHeight: 16,
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 217, 255, 0.1)',
    padding: 10,
    borderRadius: 6,
    marginTop: 8,
    gap: 8,
    borderWidth: 1,
    borderColor: 'rgba(0, 217, 255, 0.3)',
  },
  infoBoxText: {
    flex: 1,
    fontSize: 12,
    color: '#00D9FF',
    lineHeight: 16,
  },
});

export default FileUploaderTest;

